import { useEffect, useMemo, useState } from 'react';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { capitalize } from 'switch-fe-shared/utils/formatting';
import { AggregateP1SplitGroupBy } from 'switch-shared/modules/crud/utility-data/utility-data.dto';
import {
  UtilityType,
  convertToAndFromBase,
} from 'switch-shared/modules/crud/utility/utility.enums';

import { useDashboard } from '../../../../context/dashboardContext';
import { useGetAggregateP1SplitQuery } from '../../../../services/switch-rest-api/utility-data.service';
import { getUtilityUnits } from '../../../../utils/formatters';
import {
  isAllSelection,
  isAllUtilitySelection,
  toConcreteUtilityType,
} from '../../../../utils/selection';
import MetricCard from './MetricCard';

interface TrendData {
  value: number;
  percentageChange: number;
  isIncreasing: boolean;
}

interface BreakdownItem {
  label: string;
  value?: number;
  unit: string;
  trend?: TrendData;
}

interface BulkMetricWidgetProps {
  hideTrends?: boolean;
}

const BulkMetricWidget = ({ hideTrends = false }: BulkMetricWidgetProps) => {
  const { utility, dateRange, bucketOptions, zone } = useDashboard();

  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();

  const unit = useMemo(() => {
    return getUtilityUnits(utility, true);
  }, [utility]);

  const [bulk, setBulk] = useState<number | undefined>(undefined);
  const [trend, setTrend] = useState<TrendData | undefined>(undefined);
  const [breakdown, setBreakdown] = useState<BreakdownItem[] | undefined>(
    undefined
  );
  const groupBy = isAllUtilitySelection(utility)
    ? AggregateP1SplitGroupBy.Application
    : AggregateP1SplitGroupBy.Bucket;

  // Fetch data based on current selection
  const {
    data: utilityData,
    isLoading,
    isFetching,
  } = useGetAggregateP1SplitQuery(
    {
      startTimeISO: dateRange.start.toISOString(),
      endTimeISO: dateRange.end.toISOString(),
      applicationGroupId: appGroupId,
      applicationId: isAllSelection(appId) ? undefined : appId,
      utilityType: toConcreteUtilityType(utility),
      bucketSize: bucketOptions.size,
      bucketUnit: bucketOptions.unit,
      groupBy,
      zoneId: zone?._id,
      includePriorPeriod: !hideTrends,
    },
    {
      pollingInterval: 300_000,
      skip: !appGroupId && isAllSelection(appId),
    }
  );

  useEffect(() => {
    if (!utilityData) return;

    if (isAllUtilitySelection(utility)) {
      const utilityTypes = [
        UtilityType.ELECTRICITY,
        UtilityType.WATER,
        UtilityType.GAS,
      ];
      const nextBreakdown: BreakdownItem[] = [];

      utilityTypes.forEach((utilityType) => {
        const bulkKey = `${utilityType}DeltaP1Bulk`;
        const utilityTypeCap =
          utilityType.charAt(0).toUpperCase() + utilityType.slice(1);
        const priorBulkKey = `prior${utilityTypeCap}DeltaP1Bulk`;

        let totalBulk = 0;
        let priorTotalBulk = 0;
        let hasData = false;
        let hasPriorData = false;

        utilityData.buckets?.forEach((bucket: any) => {
          if (bucket[bulkKey] !== undefined) {
            hasData = true;
            totalBulk += bucket[bulkKey] || 0;
          }

          if (bucket[priorBulkKey] !== undefined) {
            hasPriorData = true;
            priorTotalBulk += bucket[priorBulkKey] || 0;
          }
        });

        if (!hasData) return;

        const currentValue =
          convertToAndFromBase(totalBulk, utilityType as any, true) || 0;
        const priorValue = hasPriorData
          ? convertToAndFromBase(priorTotalBulk, utilityType as any, true) || 0
          : undefined;

        nextBreakdown.push({
          label: capitalize(utilityType),
          value: currentValue,
          unit: getUtilityUnits(utilityType as any, true),
          trend:
            priorValue !== undefined && priorValue > 0
              ? {
                  value: currentValue,
                  percentageChange:
                    ((currentValue - priorValue) / priorValue) * 100,
                  isIncreasing: currentValue > priorValue,
                }
              : undefined,
        });
      });

      setBreakdown(nextBreakdown);
      setBulk(undefined);
      setTrend(undefined);
      return;
    }

    const totalBulk = utilityData?.totalDeltaP1Bulk;
    const priorBulk = utilityData?.priorTotalDeltaP1Bulk;

    const currentValue =
      totalBulk != null ? convertToAndFromBase(totalBulk, utility, true) : 0;

    setBulk(currentValue);

    if (priorBulk !== undefined) {
      const priorValue =
        priorBulk != null ? convertToAndFromBase(priorBulk, utility, true) : 0;

      if (priorValue > 0) {
        const percentageChange =
          ((currentValue - priorValue) / priorValue) * 100;
        setTrend({
          value: currentValue,
          percentageChange,
          isIncreasing: percentageChange > 0,
        });
      }
    }

    setBreakdown(undefined);
  }, [utilityData, utility]);

  return (
    <MetricCard
      title='Total Bulk'
      value={bulk}
      unit={unit}
      trend={trend}
      breakdown={breakdown}
      isLoading={isLoading || isFetching}
      hideTrends={hideTrends}
    />
  );
};

export default BulkMetricWidget;
